import React, { useState, useMemo, useRef } from 'react';
import { saveAs } from 'file-saver';
import { FiDownload, FiSearch, FiX } from 'react-icons/fi';

interface TableDataDisplayProps {
  data: any[];
  maxHeight?: string;
  initialRowsPerPage?: number;
  enableGrouping?: boolean;
  title?: string;  // Optional title for the table
}

const formatValue = (key: string, value: any) => {
  if (typeof value !== 'number') return String(value);

  const lowerKey = key.toLowerCase();

  // Format as percentage
  if (lowerKey.includes('percentage') || lowerKey.includes('pct') || lowerKey.includes('margin')) {
    return `${value.toFixed(2)}%`;
  }

  // Format as ratio
  if (lowerKey.includes('ratio')) {
    return `${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }

  // Add a dollar sign for costs, prices, etc.
  if (
    lowerKey.includes('revenue') ||
    lowerKey.includes('cost') ||
    lowerKey.includes('profit') ||
    lowerKey.includes('amount') ||
    lowerKey.includes('value') ||
    lowerKey.includes('margin') ||
    lowerKey.includes('price')
  ) {
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }

  // Format as number with commas and 1 decimal for totals, cost, margin, revenue
  if (
    lowerKey.includes('total') ||
    lowerKey.includes('volume') ||
    lowerKey.includes('count') ||
    lowerKey.includes('quantity') ||
    lowerKey.includes('number') ||
    lowerKey.includes('gross') ||
    lowerKey.includes('average') ||
    lowerKey.includes('avg') ||
    lowerKey.includes('containers')
  ) {
      return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 });
  }

  // if (
  //   lowerKey.includes('count') ||
  //   lowerKey.includes('number') ||
  //   lowerKey.includes('margin') ||
  //   lowerKey.includes('quantity')
  // ) {
  //   return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 });
  // }

  // if (
  //   key.includes('total') &&
  //   (key.includes('revenue') || key.includes('cost') || key.includes('margin') || key.includes('profit') || key.includes('amount') || key.includes('value') || key.includes('price'))
  // ) {
  //   return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // }

  // Format as currency if key contains BOTH 'total' AND (revenue|cost|margin|profit|amount|value|price)
  // const lowerKey = key.toLowerCase();
  // if (
  //   lowerKey.includes('total') &&
  //   (
  //     lowerKey.includes('revenue') ||
  //     lowerKey.includes('cost') ||
  //     lowerKey.includes('margin') ||
  //     lowerKey.includes('profit') ||
  //     lowerKey.includes('amount') ||
  //     lowerKey.includes('value') ||
  //     lowerKey.includes('price')
  //   )
  // ) {
  //   return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // }

  // // // Otherwise, format as number with commas and 2 decimals
  // return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // Default: just show number
  return value.toString();
};

const TableDataDisplay: React.FC<TableDataDisplayProps> = ({ 
  data, 
  maxHeight = '400px',
  initialRowsPerPage = 10,
  enableGrouping = true,
  title
}) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortConfig, setSortConfig] = useState<{key: string; direction: 'asc' | 'desc'} | null>(null);
  const [groupingEnabled, setGroupingEnabled] = useState(enableGrouping);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const headers = useMemo(() => {
    if (data.length === 0) return [];
    return Object.keys(data[0]);
  }, [data]);

  // Auto-detect groupable columns based on consecutive repeated values and column name patterns
  const detectableGroupColumns = useMemo(() => {
    if (data.length < 2) return new Set<string>();
    
    const groupables = new Set<string>();
    
    // Priority columns that are likely to be grouping keys
    const priorityPatterns = [
      /customer/i, /client/i, /account/i, /name/i, /company/i,
      /category/i, /type/i, /class/i, /group/i, /segment/i,
      /vertical/i, /industry/i, /region/i, /country/i, /location/i
    ];
    
    headers.forEach(header => {
      // Check if it's a priority column
      const isPriority = priorityPatterns.some(pattern => pattern.test(header));
      
      // Count consecutive repeated values
      let consecutiveGroups = 0;
      let currentValue = data[0][header];
      let currentGroupSize = 1;
      
      for (let i = 1; i < Math.min(data.length, 100); i++) { // Check first 100 rows for performance
        if (data[i][header] === currentValue) {
          currentGroupSize++;
        } else {
          if (currentGroupSize > 1) consecutiveGroups++;
          currentValue = data[i][header];
          currentGroupSize = 1;
        }
      }
      if (currentGroupSize > 1) consecutiveGroups++;
      
      // Add to groupable if it has consecutive groups or is a priority column with repeats
      const hasRepeats = new Set(data.slice(0, 100).map(row => row[header])).size < data.slice(0, 100).length;
      if (consecutiveGroups > 0 || (isPriority && hasRepeats)) {
        groupables.add(header);
      }
    });
    
    return groupables;
  }, [data, headers]);

  // Active groupable columns (only when grouping is enabled)
  const groupableColumns = useMemo(() => {
    return groupingEnabled ? detectableGroupColumns : new Set<string>();
  }, [detectableGroupColumns, groupingEnabled]);

  // Filter data by search text
  const filteredData = useMemo(() => {
    if (!searchText.trim()) return data;
    const lower = searchText.toLowerCase();
    return data.filter(row =>
      headers.some(h =>
        row[h] !== null && row[h] !== undefined && String(row[h]).toLowerCase().includes(lower)
      )
    );
  }, [data, searchText, headers]);

  // Sort data if needed - with special handling for grouped data
  const sortedData = useMemo(() => {
    let result = [...filteredData];
    
    // If grouping is enabled, first sort by grouped columns to ensure consecutive grouping
    if (groupingEnabled && groupableColumns.size > 0) {
      const groupedCols = Array.from(groupableColumns).sort(); // Sort column names for consistent ordering
      result = result.sort((a, b) => {
        // First sort by all grouped columns in alphabetical order of column names
        for (const col of groupedCols) {
          const aVal = String(a[col] || ''); // Convert to string for consistent comparison
          const bVal = String(b[col] || '');
          if (aVal < bVal) return -1;
          if (aVal > bVal) return 1;
        }
        return 0;
      });
    }
    
    // Then apply user-requested sorting (only if not sorting a grouped column)
    if (sortConfig && (!groupingEnabled || !groupableColumns.has(sortConfig.key))) {
      result = result.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return result;
  }, [filteredData, sortConfig, groupingEnabled, groupableColumns]);

  // Recalculate rowspans for the current page data
  const currentPageRowSpans = useMemo(() => {
    if (!groupingEnabled || groupableColumns.size === 0) return new Map();
    
    const rowSpans = new Map<string, number[]>();
    const startIdx = page * rowsPerPage;
    const endIdx = Math.min(startIdx + rowsPerPage, sortedData.length);
    const pageData = sortedData.slice(startIdx, endIdx);
    
    // Process each groupable column independently
    groupableColumns.forEach(column => {
      const spans: number[] = new Array(pageData.length).fill(1);
      let i = 0;
      
      while (i < pageData.length) {
        const currentValue = pageData[i][column];
        let spanCount = 1;
        
        // Count consecutive rows with the same value for this specific column
        while (i + spanCount < pageData.length && 
               pageData[i + spanCount][column] === currentValue) {
          spanCount++;
        }
        
        // Set rowspan for the first occurrence, mark others as hidden
        if (spanCount > 1) {
          spans[i] = spanCount;
          for (let j = i + 1; j < i + spanCount; j++) {
            spans[j] = 0; // Hidden cells
          }
        }
        
        i += spanCount;
      }
      // console.log(`Row spans for column "${column}":`, spans);
      rowSpans.set(column, spans);
    });
    
    return rowSpans;
  }, [sortedData, page, rowsPerPage, groupableColumns, groupingEnabled]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // Focus input when search opens
  const handleOpenSearch = () => {
    setSearchOpen(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  };
  // Reset page when search text or rowsPerPage changes
  const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setPage(0);
  };
  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = parseInt(e.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };
  // Close search
  const handleCloseSearch = () => {
    setSearchText('');
    setSearchOpen(false);
    setPage(0);
  };

  // CSV Download handler
  const downloadCSV = () => {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','),
      ...data.map(row => headers.map(h => {
        const val = row[h];
        if (val === null || val === undefined) return '';
        // Escape quotes and commas
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(','))
    ];
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'table_data.csv');
  };

  // Reset page when grouping is toggled to ensure proper display
  const handleGroupingToggle = () => {
    setGroupingEnabled(!groupingEnabled);
    setPage(0); // Reset to first page when toggling grouping
  };

  // Restore requestSort handler for sorting
  const requestSort = (key: string) => {
    // Don't allow sorting on grouped columns when grouping is enabled
    if (groupingEnabled && groupableColumns.has(key)) {
      return;
    }
    
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div className="rounded-xl overflow-hidden border border-neutral-dark dark:border-dark-border">
      <div className="bg-neutral-dark dark:bg-dark-secondary px-4 py-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-black dark:text-dark-textPrimary">
              {title || 'Query Results'}
            </h3>
            <div className="relative h-7 w-7 flex items-center justify-center">
              <button
                className={`absolute left-0 top-0 h-7 w-7 flex items-center justify-center transition-opacity duration-200 ${searchOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                onClick={handleOpenSearch}
                title="Search"
                aria-label="Search"
                tabIndex={searchOpen ? -1 : 0}
                style={{zIndex: 2}}
              >
                <FiSearch size={18} />
              </button>
              <div
                className={`absolute left-0 top-0 h-7 flex items-center bg-white dark:bg-dark-accent border border-neutral-dark dark:border-dark-border rounded transition-all duration-200 ${searchOpen ? 'w-40 px-2 opacity-100' : 'w-7 px-0 opacity-0 pointer-events-none'}`}
                style={{zIndex: 1}}
              >
                <FiSearch size={16} className="text-text-tertiary dark:text-dark-textSecondary mr-1" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchText}
                  onChange={handleSearchTextChange}
                  placeholder="Search..."
                  className="text-xs bg-transparent outline-none text-text-primary dark:text-white w-24 transition-width duration-200"
                  style={{minWidth: searchOpen ? '5rem' : 0, width: searchOpen ? '6rem' : 0, paddingLeft: searchOpen ? 0 : 0, border: 'none', opacity: searchOpen ? 1 : 0}}
                  tabIndex={searchOpen ? 0 : -1}
                />
                <button
                  className="ml-1 text-text-tertiary dark:text-dark-textSecondary hover:text-text-primary dark:hover:text-white focus:outline-none"
                  onClick={handleCloseSearch}
                  title="Close search"
                  aria-label="Close search"
                  tabIndex={searchOpen ? 0 : -1}
                >
                  <FiX size={14} />
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {enableGrouping && detectableGroupColumns.size > 0 && (
              <button
                onClick={handleGroupingToggle}
                className={`p-1 text-xs rounded px-2 py-1 transition-colors ${
                  groupingEnabled 
                  ? 'bg-neutral-light dark:bg-dark-accent text-text-tertiary dark:text-dark-textSecondary hover:bg-white dark:hover:bg-dark-border'
                  : 'bg-blue-500 text-white hover:bg-blue-600' 
                }`}
                title={groupingEnabled ? "Disable grouping" : "Enable grouping"}
                aria-label={groupingEnabled ? "Disable grouping" : "Enable grouping"}
              >
                {groupingEnabled ? "Ungroup" : "Group"}
              </button>
            )}
            <button
              onClick={downloadCSV}
              className="p-1 text-text-tertiary dark:text-dark-textSecondary hover:text-text-primary dark:hover:text-white focus:outline-none disabled:opacity-50"
              disabled={data.length === 0}
              title="Download CSV"
              aria-label="Download CSV"
            >
              <FiDownload size={18} />
            </button>
            <div className="text-xs text-text-tertiary dark:text-dark-textSecondary">
              {filteredData.length} rows found
              {groupingEnabled && detectableGroupColumns.size > 0 && (
                <span className="ml-2 text-blue-600 dark:text-blue-400">
                  • {detectableGroupColumns.size} column{detectableGroupColumns.size !== 1 ? 's' : ''} grouped
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {filteredData.length > 0 ? (
        <>
          <div className="overflow-x-auto bg-white dark:bg-dark-secondary" style={{ maxHeight }}>
            <table className="min-w-full divide-y divide-neutral-dark dark:divide-dark-border">
              <thead className="bg-neutral dark:bg-dark-border sticky top-0">
                <tr>
                  {headers.map((header, idx) => {
                    const isGrouped = groupingEnabled && groupableColumns.has(header);
                    const canSort = !isGrouped;
                    return (
                      <th 
                        key={idx}
                        className={`px-4 py-2 text-left text-xs font-medium text-text-secondary dark:text-dark-textSecondary tracking-wider ${
                          canSort ? 'cursor-pointer hover-glow-sm' : 'cursor-default'
                        } ${isGrouped ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                        onClick={() => canSort && requestSort(header)}
                      >
                        <div className="flex items-center">
                          {header}
                          {/* {isGrouped && (
                            <span className="ml-1 text-blue-500 text-xs">●</span>
                          )} */}
                          {canSort && sortConfig?.key === header && (
                            <span className="ml-1">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral dark:divide-dark-border">
                {paginatedData.map((row, rowIdx) => (
                  <tr key={rowIdx} className="hover-glow-sm">
                    {headers.map((header, colIdx) => {
                      const isGrouped = groupingEnabled && groupableColumns.has(header);
                      
                      if (isGrouped) {
                        const rowSpan = currentPageRowSpans.get(header)?.[rowIdx];
                        // console.log(`cell for header "${header}" at row ${rowIdx} and value ${row[header]} and rowspan ${rowSpan} out of ${currentPageRowSpans.get(header)}`);
                        
                        // If rowspan is 0, this cell is part of a previous rowspan - skip it
                        if (rowSpan === 0) {
                          return null;
                        }
                        
                        // Render the cell with appropriate rowspan
                        return (
                          <td 
                            key={colIdx} 
                            className={`px-4 py-2 text-sm text-text-primary dark:text-white ${
                              rowSpan > 1 ? 'bg-blue-50/50 dark:bg-blue-900/10 border-l-2 border-blue-200 dark:border-blue-700' : ''
                            }`}
                            rowSpan={rowSpan}
                          >
                            {row[header] !== null && row[header] !== undefined
                              ? formatValue(header, row[header])
                              : 'NULL'}
                          </td>
                        );
                      } else {
                        // Non-grouped column - always render normally
                        return (
                          <td 
                            key={colIdx} 
                            className="px-4 py-2 text-sm text-text-primary dark:text-white"
                          >
                            {row[header] !== null && row[header] !== undefined
                              ? formatValue(header, row[header])
                              : 'NULL'}
                          </td>
                        );
                      }
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="bg-neutral dark:bg-dark-secondary px-4 py-2 flex justify-between items-center">
            <div className="flex items-center">
              {totalPages > 1 && (
                <>
                  <button 
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="text-xs bg-neutral-light hover:bg-white dark:bg-dark-accent dark:hover:bg-dark-border px-2 py-1 rounded disabled:opacity-50 mr-2"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-text-secondary dark:text-dark-textSecondary">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button 
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page === totalPages - 1 || totalPages === 0}
                    className="text-xs bg-neutral-light hover:bg-white dark:bg-dark-accent dark:hover:bg-dark-border px-2 py-1 rounded disabled:opacity-50 ml-2"
                  >
                    Next
                  </button>
                </>
              )}
            </div>
            
            <div className="flex items-center">
              <label htmlFor="rowsPerPage" className="text-xs text-text-secondary dark:text-dark-textSecondary mr-2">
                Rows per page:
              </label>
              <select
                id="rowsPerPage"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="text-xs bg-neutral-light dark:bg-dark-accent border border-neutral-dark dark:border-dark-border rounded p-1"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </>
      ) : (
        <div className="p-4 text-sm text-center text-text-secondary dark:text-dark-textSecondary bg-white dark:bg-dark-secondary">
          No data available
        </div>
      )}
    </div>
  );
};

export default TableDataDisplay;
